import { Check, X, Loader2, Flag } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

export default function MainWorkSpace() {
  const tab = ["Received", "Accepted", "Declined", "My Requests"];
  const [activeTab, setActiveTab] = useState("Received");
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  // Flag modal state
  const [flagModal, setFlagModal] = useState({ open: false, eventId: null });
  const [flagReason, setFlagReason] = useState("");
  const [flagLoading, setFlagLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchRequests = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    setIsLoading(true);
    try {
      let endpoint = "";
      let status = null;

      if (activeTab === "Received") {
        endpoint = API_ENDPOINTS.NONPROFIT_GET_RECEIVED_REQUESTS;
        status = "PENDING";
      } else if (activeTab === "Accepted") {
        endpoint = API_ENDPOINTS.NONPROFIT_GET_RECEIVED_REQUESTS;
        status = "ACCEPTED";
      } else if (activeTab === "Declined") {
        endpoint = API_ENDPOINTS.NONPROFIT_GET_RECEIVED_REQUESTS;
        status = "REJECTED";
      } else if (activeTab === "My Requests") {
        endpoint = API_ENDPOINTS.NONPROFIT_GET_SENT_REQUESTS;
      }

      const url = status
        ? `${BASE_URL}${endpoint}?status=${status}`
        : `${BASE_URL}${endpoint}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to fetch requests");
      }

      const requestsData = result?.data || result || [];

      // For "My Requests" tab, expand each request into individual recipient cards
      if (activeTab === "My Requests") {
        const expandedRequests = [];
        requestsData.forEach((request) => {
          if (request.recipients && request.recipients.length > 0) {
            request.recipients.forEach((recipient) => {
              expandedRequests.push({
                ...request,
                recipient: recipient,
                recipients: [recipient],
              });
            });
          }
        });
        setRequests(expandedRequests);
      } else {
        setRequests(requestsData);
      }
    } catch (error) {
      console.error("Fetch requests error:", error);
      toast.error(error?.message || "Failed to load requests");
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async (recipientId) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_ACCEPT_COLLABORATION_REQUEST}/${recipientId}/accept`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to accept request");
      }

      toast.success("Request accepted successfully");
      fetchRequests();
    } catch (error) {
      console.error("Accept request error:", error);
      toast.error(error?.message || "Failed to accept request");
    }
  };

  const handleReject = async (recipientId) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_REJECT_COLLABORATION_REQUEST}/${recipientId}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to reject request");
      }

      toast.success("Request rejected successfully");
      fetchRequests();
    } catch (error) {
      console.error("Reject request error:", error);
      toast.error(error?.message || "Failed to reject request");
    }
  };

  const handleFlagSubmit = async () => {
    if (!flagReason.trim()) {
      toast.error("Please enter a reason for flagging.");
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      return;
    }

    setFlagLoading(true);
    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.NONPROFIT_FLAG_EVENT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ eventId: flagModal.eventId, reason: flagReason }),
      });

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || "Failed to flag event");
      }

      // Optimistically mark event as flagged in local state
      setRequests((prev) =>
        prev.map((r) =>
          r.event?.id === flagModal.eventId
            ? { ...r, event: { ...r.event, isFlagged: true } }
            : r
        )
      );

      toast.success("Event flagged successfully.");
      setFlagModal({ open: false, eventId: null });
      setFlagReason("");
    } catch (error) {
      toast.error(error?.message || "Failed to flag event");
    } finally {
      setFlagLoading(false);
    }
  };

  const closeFlagModal = () => {
    setFlagModal({ open: false, eventId: null });
    setFlagReason("");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "text-[#FFC107]";
      case "ACCEPTED":
        return "text-[#3FE36B]";
      case "REJECTED":
        return "text-red-500";
      default:
        return "text-[#61738A]";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "PENDING":
        return "Pending";
      case "ACCEPTED":
        return "Accepted";
      case "REJECTED":
        return "Rejected";
      default:
        return status;
    }
  };

  return (
    <div className="w-[95%] mx-auto mb-5">
      {/* Flag Event Modal */}
      {flagModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-[90%] max-w-md">
            <h3 className="font-popinsSemiBold text-lg mb-1">Flag this Event</h3>
            <p className="font-popinsRegular text-sm text-gray-500 mb-4">
              Describe why you find this event inappropriate. Our team will review it.
            </p>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 text-sm font-popinsRegular resize-none focus:outline-none focus:border-red-400 transition"
              rows={4}
              placeholder="Enter reason (e.g. inappropriate content, spam, misleading information)..."
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              maxLength={500}
            />
            <p className="text-xs text-gray-400 text-right mt-1">{flagReason.length}/500</p>
            <div className="flex gap-x-3 mt-4 justify-end">
              <button
                onClick={closeFlagModal}
                className="px-5 py-2 rounded-lg border font-popinsMedium text-sm text-gray-600 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleFlagSubmit}
                disabled={flagLoading}
                className="px-5 py-2 rounded-lg bg-red-500 text-white font-popinsMedium text-sm hover:bg-red-600 disabled:opacity-50 flex items-center gap-x-2 transition"
              >
                {flagLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    <Flag size={14} />
                    <span>Submit Flag</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex lg:flex-row flex-col justify-between items-center">
        <div>
          <h1 className="font-popinsBold text-2xl">Collaboration Requests</h1>
          <p className="font-popinsRegular text-textColor mt-1 text-sm text-center lg:text-left">
            Manage incoming and outgoing collaboration requests with NGOs.
          </p>
        </div>
      </div>
      <div className="mt-10">
        <div className="flex gap-x-10 overflow-x-auto panelScroll">
          {tab.map((item, i) => (
            <div
              key={i}
              className={`pb-3 cursor-pointer ${
                activeTab === item
                  ? "border-b-[4px] border-gray-300"
                  : "text-black/50"
              }`}
              onClick={() => setActiveTab(item)}
            >
              <p className="font-popinsSemiBold text-base">{item}</p>
            </div>
          ))}
        </div>
        {/* Tab Data */}
        <div className="mt-6">
          {isLoading ? (
            <div className="flex justify-center items-center mt-10">
              <Loader2 className="animate-spin text-gray-500" size={32} />
            </div>
          ) : requests.length > 0 ? (
            requests.map((request, index) => {
              const recipient = request.recipients?.[0] || request.recipient;
              const status = recipient?.status || "PENDING";
              const isReceivedTab = ["Received", "Accepted", "Declined"].includes(activeTab);
              const eventTitle = request.event?.title || "Untitled Event";
              const fromNonprofit = request.fromNonprofit?.organizationName || "Unknown Organization";
              const toNonprofit = recipient?.nonprofit?.organizationName || "Unknown Organization";
              const resourcesNeeded = request.resourcesNeeded || request.event?.description || "";
              const isFlagged = request.event?.isFlagged === true;

              const uniqueKey =
                activeTab === "My Requests" && recipient?.id
                  ? `${request.id}-${recipient.id}`
                  : `${request.id}-${index}`;

              const handleTileClick = (e) => {
                if (e.target.closest("button") || e.target.closest("a")) return;
                const url =
                  activeTab === "My Requests" && recipient?.id
                    ? `/dashboard/new-request/${request.id}?recipientId=${recipient.id}`
                    : `/dashboard/new-request/${request.id}`;
                navigate(url);
              };

              return (
                <div
                  key={uniqueKey}
                  onClick={handleTileClick}
                  className="border rounded-lg p-4 flex items-center flex-col-reverse lg:flex-row gap-4 shadow-sm hover:shadow-md transition mt-4 cursor-pointer"
                >
                  <div className="w-full lg:w-[70%]">
                    {request.isExpired && (
                      <p className="text-sm font-popinsMedium text-red-500 border border-red-500 rounded-md px-3 py-1 inline-block mb-2">
                        Event Expired
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-popinsMedium ${getStatusColor(status)} mt-2`}>
                        Status: {getStatusText(status)}
                      </p>
                      {/* Flag button — only on Received/Accepted/Declined tabs */}
                      {isReceivedTab && (
                        isFlagged ? (
                          <div className="flex items-center gap-x-1 text-red-500 mt-2" title="You have flagged this event">
                            <Flag size={15} className="fill-red-500" />
                            <span className="text-xs font-popinsMedium">Flagged</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => setFlagModal({ open: true, eventId: request.event?.id })}
                            className="flex items-center gap-x-1 text-gray-400 hover:text-red-500 transition-colors mt-2"
                            title="Flag this event as inappropriate"
                          >
                            <Flag size={15} />
                            <span className="text-xs font-popinsMedium">Flag</span>
                          </button>
                        )
                      )}
                    </div>
                    <h3 className="font-popinsSemiBold text-lg mt-1">
                      {isReceivedTab
                        ? `Request from ${fromNonprofit}`
                        : `Event: ${eventTitle}`}
                    </h3>
                    {activeTab === "My Requests" && (
                      <p className="text-sm font-popinsMedium text-gray-700 mt-1">
                        To: {toNonprofit}
                      </p>
                    )}
                    <p className="text-sm font-popinsRegular text-gray-600 mt-1">
                      {resourcesNeeded || "No additional details provided."}
                    </p>
                    {request.event && (
                      <div className="mt-2 text-xs text-gray-500">
                        <p>Event: {eventTitle}</p>
                        <p>Date: {request.event.date} at {request.event.time}</p>
                        <p>Location: {request.event.location}</p>
                      </div>
                    )}
                    {activeTab === "Received" && status === "PENDING" && (
                      <div className="flex gap-x-3 mt-5">
                        <button
                          onClick={() => handleAccept(recipient?.id)}
                          className="relative overflow-hidden py-2 bg-[#3FE36B] hover:bg-[#3FE36B]/90 text-white font-popinsSemiBold text-sm lg:text-base rounded-md shadow-lg transform transition duration-300 ease-in-out hover:scale-105 hover:shadow-[0_0_20px_rgba(63,227,107,0.6)] active:scale-95 active:shadow-inner focus:outline-none w-fit flex items-center px-4 gap-x-2"
                        >
                          <span>Accept</span>
                          <Check size={20} className="text-white" />
                          <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
                        </button>
                        <button
                          onClick={() => handleReject(recipient?.id)}
                          className="relative overflow-hidden py-2 bg-red-500 hover:bg-red-600 text-white font-popinsSemiBold text-sm lg:text-base rounded-md shadow-lg transform transition duration-300 ease-in-out hover:scale-105 hover:shadow-[0_0_20px_rgba(239,68,68,0.6)] active:scale-95 active:shadow-inner focus:outline-none w-fit flex items-center px-4 gap-x-2"
                        >
                          <span>Reject</span>
                          <X size={20} className="text-white" />
                          <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
                        </button>
                      </div>
                    )}
                    {activeTab === "Accepted" && (
                      <div className="mt-5">
                        <div className="bg-[#3FE36B] text-white font-popinsSemiBold text-sm lg:text-base py-2 px-4 rounded-md w-fit flex items-center gap-x-2">
                          <span>Accepted</span>
                          <Check size={20} className="text-white" />
                        </div>
                      </div>
                    )}
                    {activeTab === "Declined" && (
                      <div className="mt-5">
                        <div className="bg-red-500 text-white font-popinsSemiBold text-sm lg:text-base py-2 px-4 rounded-md w-fit flex items-center gap-x-2">
                          <span>Declined</span>
                          <X size={20} className="text-white" />
                        </div>
                      </div>
                    )}
                    {activeTab === "My Requests" && (
                      <div className="mt-5">
                        <div
                          className={`text-white font-popinsSemiBold text-sm lg:text-base py-2 px-4 rounded-md w-fit flex items-center gap-x-2 ${
                            status === "ACCEPTED"
                              ? "bg-[#3FE36B]"
                              : status === "REJECTED"
                              ? "bg-red-500"
                              : "bg-[#FFC107]"
                          }`}
                        >
                          <span>{getStatusText(status)}</span>
                          {status === "ACCEPTED" && <Check size={20} className="text-white" />}
                          {status === "REJECTED" && <X size={20} className="text-white" />}
                        </div>
                      </div>
                    )}
                    {/* Task List Grid */}
                    {recipient?.tasks && recipient.tasks.length > 0 && (
                      <div className="mt-5">
                        <h4 className="font-popinsSemiBold text-base mb-3">Tasks</h4>
                        <div className="overflow-x-auto rounded-lg border border-gray-300">
                          <table className="min-w-full bg-white">
                            <thead>
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-popinsRegular">Task</th>
                                <th className="px-4 py-2 text-left text-xs font-popinsRegular">Count</th>
                                <th className="px-4 py-2 text-left text-xs font-popinsRegular">Assign To</th>
                                <th className="px-4 py-2 text-left text-xs font-popinsRegular">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recipient.tasks.map((task) => (
                                <tr className="border-t" key={task.id}>
                                  <td className="px-4 py-2 text-xs font-popinsRegular">{task.taskName}</td>
                                  <td className="px-4 py-2 text-xs font-popinsRegular text-black/60">
                                    {task.countNeeded || 0}
                                  </td>
                                  <td className="px-4 py-2 text-xs font-popinsRegular text-black/60">
                                    {task.assigneeOrganizationName || recipient?.nonprofit?.organizationName || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 font-popinsRegular">
                                    <div
                                      className={`${
                                        recipient?.status === "ACCEPTED"
                                          ? "bg-[#3FE36B]"
                                          : recipient?.status === "REJECTED"
                                          ? "bg-red-500"
                                          : task.status === "IN_PROGRESS"
                                          ? "bg-[#3FE36B]"
                                          : task.status === "COMPLETED"
                                          ? "bg-[#3FC4E3]"
                                          : "bg-[#DFCD00]"
                                      } flex justify-center rounded-lg px-4 py-1 font-popinsMedium text-xs`}
                                    >
                                      {recipient?.status === "ACCEPTED"
                                        ? "Accepted"
                                        : recipient?.status === "REJECTED"
                                        ? "Rejected"
                                        : task.status === "IN_PROGRESS"
                                        ? "In Progress"
                                        : task.status === "COMPLETED"
                                        ? "Completed"
                                        : "Pending"}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="w-full lg:w-[30%]">
                    {request.event?.coverImageUrl ? (
                      !imageErrors[uniqueKey] ? (
                        <img
                          src={request.event.coverImageUrl}
                          alt={eventTitle}
                          className="w-full h-52 object-cover rounded-lg"
                          onError={() =>
                            setImageErrors((prev) => ({ ...prev, [uniqueKey]: true }))
                          }
                        />
                      ) : (
                        <div className="w-full h-44 bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-center">
                          <p className="text-red-500 text-xs text-center px-4">Image failed to load</p>
                        </div>
                      )
                    ) : (
                      <div className="w-full h-44 bg-gray-100 rounded-lg flex items-center justify-center">
                        <p className="text-gray-400 text-xs text-center px-4">No image available</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-gray-500 text-center mt-10 col-span-2">
              No records found for {activeTab}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
